import { Control, FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button, ColorScheme, Grid, GridItem } from '@hivewatch/dark'

import { ControlledSelect, ControlledTextInput } from '@/components/ui'

import { DarkEditDeviceSection } from '../../dark/DarkEditDeviceSection'
import { FormDataTypeI, Options } from '../../types/types'

interface DarkDeviceDetailsSectionIProps {
  colorScheme: ColorScheme
  control: Control<FormDataTypeI>
  errors: FieldErrors<FormDataTypeI>
  floorsOptions: Options
  isDevicePlaced: boolean
  isSaveDisabled: boolean
  isSaveLoading: boolean
  onSave: () => void
}

export const DarkDeviceDetailsSection = ({
  colorScheme,
  control,
  errors,
  floorsOptions,
  isDevicePlaced,
  isSaveDisabled,
  isSaveLoading,
  onSave,
}: DarkDeviceDetailsSectionIProps) => {
  const { t } = useTranslation()

  return (
    <DarkEditDeviceSection
      actions={
        <Button
          colorScheme={colorScheme}
          disabled={isSaveDisabled}
          isLoading={isSaveLoading}
          onClick={onSave}
        >
          {isSaveLoading ? t('common:saving') : t('common:save')}
        </Button>
      }
      colorScheme={colorScheme}
      description={t('devices:crud_configure_description')}
      title={t('devices:crud_details_connection_title')}
    >
      <Grid
        gap='16px'
        templateColumns={{
          base: '1fr',
          md: 'repeat(2, 1fr)',
          xl: 'repeat(4, 1fr)',
        }}
      >
        <GridItem>
          <ControlledTextInput
            colorScheme={colorScheme}
            control={control}
            data-testid='devicesPage_editDeviceModal_name'
            errors={errors}
            label={t('common:device_name')}
            name='name'
            placeholder={t('devices:crud_device_name_placeholder')}
            rules={{ required: t('devices:crud_device_name_required') }}
            width='100%'
          />
        </GridItem>
        <GridItem>
          <ControlledSelect
            colorScheme={colorScheme}
            control={control}
            data-testid='devicesPage_editDeviceModal_location'
            hasWarning={Boolean(errors.location)}
            isClearable
            isDisabled={isDevicePlaced}
            label={t('devices:crud_device_location_label')}
            name='location'
            options={floorsOptions}
            placeholder={t('devices:crud_device_location_select')}
            rules={{ required: true }}
            warningText={t('devices:crud_device_location_placeholder')}
          />
        </GridItem>
        <GridItem>
          <ControlledTextInput
            colorScheme={colorScheme}
            control={control}
            data-testid='devicesPage_editDeviceModal_manufacturerName'
            errors={errors}
            label={t('devices:crud_device_manufacturer_label')}
            name='manufacturerName'
            placeholder={t('devices:crud_manufacturer_placeholder')}
            width='100%'
          />
        </GridItem>
        <GridItem>
          <ControlledTextInput
            colorScheme={colorScheme}
            control={control}
            data-testid='devicesPage_editDeviceModal_externalId'
            errors={errors}
            label={t('devices:crud_external_id_label')}
            name='externalId'
            placeholder={t('devices:crud_external_id_placeholder')}
            width='100%'
          />
        </GridItem>
      </Grid>
    </DarkEditDeviceSection>
  )
}
